import * as Sentry from '@sentry/react'
import { isRecord } from '@/lib/utils'

type StripeConnectFailurePhase =
  | 'account_session'
  | 'embedded_component'
  | 'embedded_component_timeout'

function stripeConnectErrorType(error: unknown, fallback = 'unknown'): string {
  return isRecord(error) && typeof error.type === 'string' && error.type ? error.type : fallback
}

function stripeConnectHttpStatus(error: unknown): unknown {
  return isRecord(error) && isRecord(error.response) ? (error.response.status ?? null) : null
}

export function reportStripeConnectFailure({
  error,
  phase,
  surface,
  organizationId,
  elementTagName = null,
  loaderStarted = false,
}: {
  error: unknown
  phase: StripeConnectFailurePhase
  surface?: string | null
  organizationId?: string | number | null
  elementTagName?: string | null
  loaderStarted?: boolean
}) {
  const errorType = stripeConnectErrorType(
    error,
    phase === 'account_session' ? 'account_session_request_error' : 'unknown'
  )
  const httpStatus = stripeConnectHttpStatus(error)

  Sentry.withScope((scope) => {
    scope.setLevel('warning')
    // One issue per surface and phase, so onboarding failures are not buried under banner noise.
    scope.setFingerprint(['stripe-connect-load', surface ?? 'unknown', phase])
    scope.setTag('feature_area', 'stripe_connect')
    scope.setTag('stripe_connect_phase', phase)
    scope.setTag('stripe_connect_surface', surface ?? 'unknown')
    scope.setTag('stripe_connect_error_type', errorType)
    scope.setTag('stripe_connect_loader_started', String(loaderStarted))
    scope.setContext('stripe_connect', {
      organizationId: organizationId ?? null,
      surface: surface ?? null,
      phase,
      elementTagName,
      loaderStarted,
      errorType,
      httpStatus,
    })
    Sentry.captureMessage(
      phase === 'account_session'
        ? 'Stripe Connect AccountSession request failed.'
        : 'Stripe Connect embedded component failed to load.'
    )
  })
}
